import {
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show,
  untrack
} from 'solid-js';
import Tab from '~/components/ui/Tab';
import './codeblock.scss';
import PrimsBlock from '~/components/CodeBlock/PrimsBlock.tsx';
import { Button } from '~/components/ui/Button';
import { CheckCircleIcon, CopyIcon } from 'lucide-solid';
import {
  arrayToObjectByKey,
  copyTextIntoClipboard
} from '~/utils/commonUtils.ts';
import { globalStore, setGlobalStore } from '~/stores';
import { miscConstants } from '~/consts.ts';

export enum CodeBlockLanguages {
  JAVASCRIPT = 'javascript',
  PYTHON = 'py',
  JAVA = 'java',
  GO = 'go',
  BASH = 'BASH',
  XML = 'xml',
  JSON = 'json'
}

export const LANGUAGE_DISPLAY_NAMES = {
  [CodeBlockLanguages.JAVASCRIPT]: 'Node.js',
  [CodeBlockLanguages.PYTHON]: 'Python',
  [CodeBlockLanguages.JAVA]: 'Java',
  [CodeBlockLanguages.GO]: 'Go',
  [CodeBlockLanguages.XML]: 'XML',
  [CodeBlockLanguages.BASH]: 'cURL',
  [CodeBlockLanguages.JSON]: 'JSON'
};

type LanguageConfig = {
  language: CodeBlockLanguages;
  content: string;
  languageSelectionTracker: CodeBlockLanguages;
  enabled?: boolean;
  displayName?: string;
  showLineNumbers?: boolean;
  className?: string;
};

type CodeEmbedProps = {
  languageConfigs: LanguageConfig[];
};

export default function CodeBlock(props: CodeEmbedProps): JSXElement {
  const merged = mergeProps({}, props);

  if (!merged.languageConfigs.length) {
    throw new Error('languageConfigs array cannot be empty');
  }

  const filterSelectedLanguage = () =>
    merged.languageConfigs.find(
      ({ enabled, languageSelectionTracker }) =>
        !!enabled ||
        languageSelectionTracker === globalStore.codeBlockSelectedLanguage
    );

  const [selectedLanguage, setSelectedLanguage] = createSignal<LanguageConfig>(
    filterSelectedLanguage() ?? merged.languageConfigs[0]
  );

  createEffect(() => {
    if (globalStore.codeBlockSelectedLanguage) {
      untrack(() => {
        const language = filterSelectedLanguage();
        language && setSelectedLanguage(language);
      });
    }
  });

  const [copied, setCopied] = createSignal<boolean>(false);
  let timerValue: ReturnType<typeof setTimeout>;

  createEffect(() => {
    if (copied()) {
      clearTimeout(timerValue);
      timerValue = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  });

  const copyClickHandler = (e: Event, content: string) => {
    e.stopPropagation();
    copyTextIntoClipboard(content).then(() => !copied() && setCopied(true));
  };

  const getTabList = () =>
    merged.languageConfigs.map(
      ({
        language,
        languageSelectionTracker,
        content,
        displayName,
        showLineNumbers,
        className
      }) => ({
        id: languageSelectionTracker,
        class: className,
        label: displayName ?? LANGUAGE_DISPLAY_NAMES[language],
        content: () => (
          <div class={`w-full`}>
            <PrimsBlock
              language={language}
              content={content}
              showLineNumbers={showLineNumbers}
            />
          </div>
        )
      })
    );

  const [tabList, setTabList] = createSignal(getTabList());
  const [tabInvalidator, setTabInvalidator] = createSignal(
    new Date().toISOString()
  );

  createEffect(() => {
    if (merged.languageConfigs) {
      untrack(() => {
        setTabList((tabList) => {
          const updatedLanguageConfigMap = arrayToObjectByKey(
            getTabList(),
            'id'
          );
          tabList.forEach((tab) => {
            if (updatedLanguageConfigMap[tab.id] !== undefined) {
              tab.content = updatedLanguageConfigMap[tab.id].content;
            }
          });
          return tabList;
        });
        setTabInvalidator(new Date().toISOString());
      });
    }
  });

  return (
    <div class={`code-block-container relative`}>
      <div class={`absolute right-6 top-[15px] z-10`}>
        <Show
          when={!copied()}
          fallback={
            <CheckCircleIcon
              width={18}
              class={`text-success`}
            />
          }
        >
          <Button
            type={'button'}
            variant={'link'}
            class={` text-fg-subtle hover:text-fg-default`}
            onClick={(e) => copyClickHandler(e, selectedLanguage().content)}
          >
            <CopyIcon width={18} />
          </Button>
        </Show>
      </div>
      <Tab
        class={`max-h-[300px]`}
        lazyMount={true}
        list={tabList()}
        value={selectedLanguage().languageSelectionTracker}
        onValueChange={({ value }) => {
          localStorage.setItem(
            miscConstants.localStorageKeys.getCodeBlockLanguageKey(),
            value
          );
          setGlobalStore(
            'codeBlockSelectedLanguage',
            value as CodeBlockLanguages
          );
        }}
        invalidator={tabInvalidator()}
      />
    </div>
  );
}
